import {
  getExternalCalendarEventPreview,
  getExternalEventResponseState,
  isExternalEventAllDay,
} from '@griply/utils';
import React, { useMemo, useRef } from 'react';
import {
  useDuplicateEvent,
  useExternalEventMenu,
} from 'shared/contexts/event-form';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { useOpenExternalEventPopup } from 'shared/hooks/use-open-external-event-popup';
import { ExternalCalendarEvent } from 'shared/types/external-calendar-event';

import * as Styled from './external-events-list.style';

export type ExternalEventRowProps = {
  event: ExternalCalendarEvent;
  label: string;
  isPast: boolean;
};

export const ExternalEventRow: React.FC<ExternalEventRowProps> = ({
  event,
  label,
  isPast,
}) => {
  const containerRef = useRef<HTMLAnchorElement>(null);
  const allDay = isExternalEventAllDay(event);
  const preview = useMemo(
    () => getExternalCalendarEventPreview(event),
    [event],
  );

  const { eventTarget, onOpen } = useOpenExternalEventPopup(containerRef, {
    externalId: event.id,
    title: event.title,
    allDay,
    start: event.start.dateTime ?? event.start.date ?? undefined,
    end: event.end.dateTime ?? event.end.date ?? undefined,
    connectionId: event.connectionId,
    calendarId: event.calendarId,
    url: event.url,
    preview,
  });

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({ disabled: !eventTarget });

  const duplicateEvent = useDuplicateEvent();
  const ExternalEventMenu = useExternalEventMenu();
  const onDuplicate = eventTarget
    ? () => duplicateEvent(eventTarget)
    : undefined;

  return (
    <Styled.ListItem>
      <Styled.EventContainer
        ref={containerRef}
        as={eventTarget ? 'button' : !event.url ? 'span' : undefined}
        type={eventTarget ? 'button' : undefined}
        onClick={eventTarget ? onOpen : undefined}
        onContextMenu={openMenu}
        href={eventTarget ? undefined : event.url}
        target={eventTarget ? undefined : '_blank'}
        rel={eventTarget ? undefined : 'noreferrer'}
        $isPast={isPast}
      >
        <Styled.Color
          $color={event.color}
          $responseState={getExternalEventResponseState(event)}
        />
        <Styled.Time>{label}</Styled.Time>
        <Styled.Title>{event.title}</Styled.Title>
      </Styled.EventContainer>

      {!!menuPosition && !!eventTarget && (
        <ExternalEventMenu
          eventTarget={eventTarget}
          isRecurring={!!event.isRecurring}
          isOrganizer={event.isOrganizer}
          location={menuPosition}
          onEdit={onOpen}
          onDuplicate={onDuplicate}
          onClose={closeMenu}
        />
      )}
    </Styled.ListItem>
  );
};
